/**
 * Thermal Receipt Generator Component
 * Version: 1.0.0
 * Last Updated: 2025-12-01
 *
 * Description:
 *   Generate thermal receipt PDFs from order lists
 *   - File upload with order number validation
 *   - Fetches order details from database
 *   - Auto duplicate removal
 *   - Batch processing (25 receipts per PDF, ZIP if >25)
 *   - 110mm thermal receipt format
 */

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip,
} from '@mui/material';
import {
    Upload as UploadIcon,
    Download as DownloadIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckCircleIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { b2cOpsAPI } from '../../api';

// No configuration needed for thermal receipts - fixed format

interface PreviewRow {
    id: number;
    order_number: string;
    customer_name: string;
    total: string;
    status: string;
}

interface PreviewData {
    total_entries: number;
    duplicates_removed: number;
    valid_receipts: number;
    invalid_orders: number;
    preview_data: PreviewRow[];
    all_order_numbers: string[];
}

export default function ThermalReceiptGenerator() {
    const { enqueueSnackbar } = useSnackbar();
    const gridRef = useGridApiRef();

    // State
    const [file, setFile] = useState<File | null>(null);
    const [previewData, setPreviewData] = useState<PreviewData | null>(null);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // No configuration state needed - thermal receipts have fixed format

    // Drag and drop handlers
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            validateAndLoadFile(droppedFile);
        }
    };

    const validateAndLoadFile = async (uploadedFile: File) => {
        // Validate file type
        const validTypes = ['.xlsx', '.xls', '.csv'];
        const fileExt = uploadedFile.name.substring(uploadedFile.name.lastIndexOf('.')).toLowerCase();

        if (!validTypes.includes(fileExt)) {
            enqueueSnackbar('Invalid file type. Please upload Excel or CSV file.', { variant: 'error' });
            return;
        }

        // Validate file size (20MB)
        if (uploadedFile.size > 20 * 1024 * 1024) {
            enqueueSnackbar('File too large. Maximum size is 20 MB.', { variant: 'error' });
            return;
        }

        setFile(uploadedFile);
        setLoading(true);

        try {
            // Call thermal receipt preview API
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const response = await fetch('/api/v1/thermal-receipt/preview', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: formData
            });
            if (!response.ok) throw new Error('Failed to preview');
            const preview = await response.json();
            setPreviewData(preview);
            enqueueSnackbar('File loaded successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to load file', { variant: 'error' });
            setFile(null);
            setPreviewData(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload (click)
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const uploadedFile = event.target.files?.[0];
        if (uploadedFile) {
            validateAndLoadFile(uploadedFile);
        }
    };

    // Handle receipt generation
    const handleGenerate = async () => {
        if (!previewData) return;

        setGenerating(true);
        try {
            const response = await fetch('/api/v1/thermal-receipt/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                },
                body: JSON.stringify({
                    order_numbers: previewData.all_order_numbers
                })
            });

            if (!response.ok) throw new Error('Failed to generate');

            const blob = await response.blob();

            // Download the file
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('content-disposition');
            let filename = 'thermal_receipts.pdf';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar('Receipts generated successfully!', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar('Failed to generate receipts', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        { field: 'id', headerName: '#', width: 70 },
        { field: 'order_number', headerName: 'Order #', width: 120 },
        { field: 'customer_name', headerName: 'Customer Name', width: 250 },
        { field: 'total', headerName: 'Total', width: 120 },
        { field: 'status', headerName: 'Status', width: 100 },
    ];

    // Prepare rows for DataGrid
    const rows = previewData?.preview_data?.map((item, idx) => ({
        ...item,
        id: idx + 1,
    })) || [];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    📄 Thermal Receipt Generator
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Generate thermal receipt PDFs from order lists
                </Typography>
            </Box>

            {/* Configuration removed - thermal receipts use fixed format */}

            {/* File Upload */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    border: isDragging ? '2px dashed #1976d2' : '2px dashed transparent',
                    bgcolor: isDragging ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s ease'
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Typography variant="h6" gutterBottom>
                    📤 Upload File
                </Typography>
                <Box sx={{ mt: 2, textAlign: 'center', py: 3 }}>
                    <input
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        id="file-upload"
                        type="file"
                        onChange={handleFileUpload}
                    />
                    <label htmlFor="file-upload">
                        <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            disabled={loading}
                            size="large"
                        >
                            {loading ? 'Loading...' : 'Choose File or Drag & Drop Here'}
                        </Button>
                    </label>
                    {file && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <CheckCircleIcon color="success" />
                            <Typography variant="body2">{file.name}</Typography>
                        </Box>
                    )}
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                    File must contain <strong>Order #</strong> column with WooCommerce order numbers
                </Alert>
            </Paper>

            {/* Preview Section */}
            {previewData && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        📊 Summary
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, mb: 3 }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Entries
                            </Typography>
                            <Typography variant="h5">{previewData.total_entries}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Duplicates Removed
                            </Typography>
                            <Typography variant="h5">{previewData.duplicates_removed}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Invalid Orders
                            </Typography>
                            <Typography variant="h5" color="error">{previewData.invalid_orders}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Receipts to Generate
                            </Typography>
                            <Typography variant="h5" color="primary">
                                {previewData.valid_receipts}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                PDF Files
                            </Typography>
                            <Typography variant="h5">
                                {Math.ceil(previewData.valid_receipts / 25)}
                                {previewData.valid_receipts > 25 && (
                                    <Chip label="ZIP" size="small" color="info" sx={{ ml: 1 }} />
                                )}
                            </Typography>
                        </Box>
                    </Box>

                    {previewData.valid_receipts > 25 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            ⚠️ Generating {previewData.valid_receipts} receipts. Will be split into{' '}
                            {Math.ceil(previewData.valid_receipts / 25)} PDFs (25 receipts each) and zipped.
                        </Alert>
                    )}

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        👀 Data Preview
                    </Typography>
                    <Box
                        className={isFullscreen ? 'datagrid-fullscreen' : ''}
                        sx={{
                            height: isFullscreen ? '100vh' : 400,
                            width: '100%',
                            transition: 'height 0.3s ease',
                        }}
                    >
                        <DataGrid
                            apiRef={gridRef}
                            rows={rows}
                            columns={columns}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 20 } },
                            }}
                            pageSizeOptions={[10, 20, 50]}
                            disableRowSelectionOnClick
                            slots={{
                                toolbar: () => (
                                    <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Button
                                            startIcon={isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            size="small"
                                        >
                                            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                                        </Button>
                                    </Box>
                                ),
                            }}
                            sx={{
                                border: '1px solid #e0e0e0',
                                height: '100%',
                                '& .MuiDataGrid-cell': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid #e0e0e0',
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                            }}
                        />
                    </Box>
                    {previewData.preview_data.length < previewData.valid_receipts && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Showing first {previewData.preview_data.length} of {previewData.valid_receipts} receipts
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={generating ? <CircularProgress size={20} /> : <DownloadIcon />}
                        onClick={handleGenerate}
                        disabled={generating}
                        fullWidth
                        sx={{ mt: 3 }}
                    >
                        {generating ? 'Generating...' : `🚀 Generate ${previewData.valid_receipts} Receipts`}
                    </Button>
                </Paper>
            )}

            {/* Help Section */}
            {!previewData && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>📖 How to Use</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" component="div">
                            <strong>Instructions:</strong>
                            <ol>
                                <li>
                                    <strong>File Format:</strong> Upload Excel (.xlsx, .xls) or CSV file with column{' '}
                                    <code>Order #</code> containing WooCommerce order numbers
                                </li>
                                <li>
                                    <strong>Validation:</strong> System fetches order details from database and validates
                                </li>
                                <li>
                                    <strong>Preview:</strong> Review data summary showing valid/invalid orders
                                </li>
                                <li>
                                    <strong>Generate:</strong> Click to create thermal receipt PDFs (or ZIP if &gt;25 receipts)
                                </li>
                            </ol>

                            <strong>Receipt Format:</strong>
                            <ul>
                                <li>110mm wide (4-inch thermal paper)</li>
                                <li>Includes: Store header, order details, items, totals, payment info</li>
                                <li>Fixed format - no configuration needed</li>
                            </ul>

                            <strong>Features:</strong>
                            <ul>
                                <li>Auto-removes duplicates and validates against database</li>
                                <li>25 receipts per PDF (multiple PDFs zipped if needed)</li>
                                <li>Professional thermal receipt layout</li>
                                <li>Ready for 4-inch thermal printers</li>
                            </ul>
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
}
